import type { Language } from "./language.ts";
import { en, type TextKey } from "./en.ts";
import { es } from "./es.ts";

const DICTIONARIES: Record<Language, Record<TextKey, string>> = { en, es };

/** Shape of `useT()`'s return value — for model-layer functions that need to produce translated text but aren't React components. */
export type Translate = (key: TextKey, params?: Record<string, string | number>) => string;

const PLACEHOLDER_RE = /\{[a-zA-Z0-9_]+\}/g;

/**
 * `t()` params aren't typed per-key (a full per-key param-shape type system
 * is a bigger type-level project than this warrants) — a typo'd placeholder
 * (`{nmae}` instead of `{name}`, on either side: the template's own text or
 * the caller's own `params` object) would otherwise silently leave the
 * literal `{nmae}` text sitting in the rendered UI with no signal anywhere.
 * This is the scoped alternative: a dev-only console warning whenever a
 * `{...}`-shaped placeholder survives substitution.
 */
function warnIfUnresolvedPlaceholder(key: TextKey, text: string): void {
  if (!import.meta.env.DEV) return;
  const unresolved = text.match(PLACEHOLDER_RE);
  if (unresolved) {
    console.warn(
      `i18n: "${key}" still has unresolved placeholder(s) ${unresolved.join(", ")} after substitution — check for a typo between the template and its params.`
    );
  }
}

/** Hook-less version of `useT()`, for places that can't call hooks (ErrorBoundary.tsx, a `class Component`). Values may carry `{name}`-style placeholders, substituted with `params.name`. */
export function translate(
  language: Language,
  key: TextKey,
  params?: Record<string, string | number>
): string {
  const text = DICTIONARIES[language][key];
  const result = params
    ? Object.entries(params).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, String(v)), text)
    : text;
  warnIfUnresolvedPlaceholder(key, result);
  return result;
}

/**
 * `t(count === 1 ? oneKey : otherKey, params)` — a plain "one"/"other" pick,
 * not real ICU MessageFormat plural rules (which also need "few"/"many"/
 * "zero" categories for some languages). EN/ES both only ever distinguish
 * singular from plural, so this covers this codebase's own real call sites
 * without pulling in a whole plural-rules library for it. `count` is also
 * threaded into `params` automatically — every real call site needs it
 * there too, and forgetting it is exactly the kind of typo `t()`'s own
 * unresolved-placeholder warning above already exists to catch.
 */
export function pluralText(
  t: (key: TextKey, params?: Record<string, string | number>) => string,
  count: number,
  oneKey: TextKey,
  otherKey: TextKey,
  params?: Record<string, string | number>
): string {
  return t(count === 1 ? oneKey : otherKey, { ...params, count });
}
